import * as React from "react";
import { Impact, ReviewTileItem } from "@/types/tile.types";
import { Badge } from "./ui/badge";
import { useFormatTile } from "@/hooks/useFormatTile";
import { Loader } from "lucide-react";
import { cn } from "@/lib/utils";
const TileActions = React.lazy(() =>
  import("./TileActions").then((module) => ({ default: module.TileActions }))
);

interface TileItemProps {
  item: ReviewTileItem;
  documentId: string;
  isBulkOperationInProgress: boolean;
  onTileClick: (item: ReviewTileItem) => void;
}

function getImpactBadgeVariant(impact: Impact): "destructive" | "warning" | "success" | "default" {
  switch (impact) {
    case "HIGH":
      return "destructive";
    case "MEDIUM":
      return "warning";
    case "LOW":
      return "success";
    default:
      return "default";
  }
}

const TileItem: React.FC<TileItemProps> = ({
  item,
  documentId,
  isBulkOperationInProgress,
  onTileClick,
}) => {
  const { formatTileContent } = useFormatTile();
  const [isItemBeingProcessed, setIsItemBeingProcessed] = React.useState(false);
  const isCompleted = item.originalData?.status?.isCompleted;
  const isFailed = item.originalData?.status?.isFailed;

  return (
    <div
      className={cn(
        "bg-muted rounded-md p-4 flex gap-3 border",
        isItemBeingProcessed && "opacity-60 pointer-events-none",
        isCompleted && "border-success-300 bg-success-300/10",
        isFailed && "border-danger-200 bg-danger-200/10"
      )}
      key={item.id}
    >
      <div
        className="flex flex-col gap-3 flex-1 cursor-pointer"
        onClick={() => onTileClick(item)}
        title="Click to search in document"
      >
        <div className="capitalize text-sm">
          <label htmlFor="" className="font-medium">
            Type
          </label>
          : <span className="text-neutral-400">{item.queryType || "---"}</span>
        </div>
        {item.impact && item.impact !== "ALL" && (
          <div className="text-sm capitalize">
            <label htmlFor="" className="font-medium">
              Impact:
            </label>
            <Badge
              className="rounded ml-1 text-xs font-medium"
              variant={getImpactBadgeVariant(item.impact)}
            >
              {item.impact.charAt(0) + item.impact.slice(1).toLowerCase()}
            </Badge>
          </div>
        )}
        {item.documentName && (
          <p className="text-sm text-neutral-400">
            <strong className="text-neutral-200">Document: </strong>
            {item.documentName}
          </p>
        )}
        <div className="whitespace-pre-line flex flex-col gap-3">{formatTileContent(item)}</div>
      </div>
      <div className="sticky bottom-3 self-end">
        <React.Suspense fallback={<Loader className="animate-spin" />}>
          <TileActions
            item={item}
            documentId={documentId}
            isBulkOperationInProgress={isBulkOperationInProgress}
            onProcessingStateChange={setIsItemBeingProcessed}
          />
        </React.Suspense>
      </div>
    </div>
  );
};

export default TileItem;
